import React, { useState } from 'react';
import { HelpCircle, MessageCircle, ChevronDown, Circle } from 'lucide-react';
import type { Faq, HelpSettings } from '../../types';
import { waLink } from '../../features/faq/api/faq';
import { HELP } from '../../constants/testIds';

export function HelpView({ faqs, help }: { faqs: Faq[]; help: HelpSettings | null }) {
  const [open, setOpen] = useState<string | null>(null);
  const categories = Array.from(new Set(faqs.map((f) => f.category)));

  return (
    <div data-testid={HELP.page} className="space-y-8">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* FAQ */}
        <div className="lg:col-span-2 space-y-6">
          <div className="flex items-center gap-2 text-white/70">
            <HelpCircle className="w-4 h-4 text-cyan-400" strokeWidth={1.5} />
            <h2 className="text-sm uppercase tracking-wider">Pertanyaan Umum</h2>
          </div>
          {faqs.length === 0 ? (
            <p className="text-white/40 text-sm bg-white/[0.02] border border-white/10 rounded-xl p-6">Belum ada FAQ.</p>
          ) : (
            categories.map((cat) => (
              <div key={cat}>
                <p className="text-xs uppercase tracking-[0.2em] text-cyan-400 mb-2">{cat}</p>
                <div className="space-y-2">
                  {faqs.filter((f) => f.category === cat).map((f) => (
                    <div key={f.id} data-testid={HELP.faqItem} className="bg-white/[0.02] border border-white/10 rounded-xl overflow-hidden">
                      <button onClick={() => setOpen(open === f.id ? null : f.id)} className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-white/[0.03] transition-colors">
                        <span className="text-white text-sm font-medium">{f.question}</span>
                        <ChevronDown className={`w-4 h-4 text-white/40 shrink-0 transition-transform ${open === f.id ? 'rotate-180' : ''}`} strokeWidth={1.5} />
                      </button>
                      {open === f.id && <div className="px-4 pb-4 text-sm text-white/60 leading-relaxed whitespace-pre-line">{f.answer}</div>}
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Help / Contact */}
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-6">
            <div className="flex items-center gap-2 text-white/70 mb-3">
              <MessageCircle className="w-4 h-4 text-cyan-400" strokeWidth={1.5} />
              <h3 className="text-sm uppercase tracking-wider">Butuh Bantuan?</h3>
            </div>
            <p className="text-sm text-white/60 mb-4">{help?.contact_message || 'Tim support kami siap membantu Anda.'}</p>
            {help?.enable_whatsapp && help.whatsapp_number && (
              <a
                href={waLink(help.whatsapp_number, help.contact_message)}
                target="_blank"
                rel="noreferrer"
                data-testid={HELP.waCta}
                className="w-full inline-flex items-center justify-center gap-2 bg-emerald-400 text-[#050914] font-medium px-5 py-3 rounded-lg transition-colors hover:bg-emerald-300"
              >
                <MessageCircle className="w-4 h-4" strokeWidth={2} /> {help.whatsapp_label || 'Chat via WhatsApp'}
              </a>
            )}
            {help?.enable_live_chat_placeholder && (
              <div data-testid={HELP.liveChat} className="mt-3 flex items-center gap-2 text-xs text-white/50 bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2">
                <Circle className="w-2.5 h-2.5 text-emerald-400 fill-emerald-400" />
                {help.live_chat_message_placeholder || 'Live chat tersedia saat admin online.'}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
